/**
 * Google OAuth Callback (direct, FitFrik's own credentials).
 * Receives `?code=&state=` from Google, exchanges via backend for our JWT.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { apiClient } from "../../services/api";
import { useGuestStore, setGuestMode, trackGuest } from "../../store/guestStore";

export default function GoogleCallback() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error");

    if (oauthError) {
      setError("Sign-in cancelled. Please try again.");
      return;
    }
    if (!code || !state) {
      navigate("/login", { replace: true });
      return;
    }

    apiClient
      .post("/auth/google/callback", { code, state })
      .then(async ({ data }) => {
        // Persist token + user using the SAME storage keys as email login (authStore).
        if (data?.token) localStorage.setItem("ff_token", data.token);
        if (data?.user) setUser(data.user);

        // Iter 51: migrate any pending guest scans NOW that we hold a JWT.
        // Non-fatal — a failure here must never block sign-in navigation.
        try {
          const gs = useGuestStore.getState();
          const pending = gs.pendingFoodScans || [];
          if (pending.length > 0) {
            const shaped = pending
              .filter((s) => s.client_scan_id && s.dish_name)
              .map((s) => ({
                client_scan_id: s.client_scan_id,
                dish_name: s.dish_name,
                calories: s.calories || 0,
                protein_g: s.protein_g || 0,
                carbs_g: s.carbs_g || 0,
                fat_g: s.fat_g || 0,
                portion_multiplier: s.portion_multiplier || 1,
                scanned_at: s.scanned_at || new Date().toISOString(),
                thumbnail_base64: s.thumbnail_base64 || null,
              }));
            if (shaped.length > 0 || (gs.savedProducts || []).length > 0) {
              try {
                await apiClient.post("/guest/migrate", {
                  anon_id: gs.anon_id, scans: shaped,
                  saved_products: (gs.savedProducts || []).map((r) => ({
                    canonical: r.canonical, saved_at: r.saved_at,
                  })),
                });
              } catch { /* migrate best-effort */ }
            }
            try {
              trackGuest("signup_completed", {
                context: "google_callback",
                retroactive_save_attempted: true,
                retroactive_save_succeeded: true,
              });
            } catch { /* noop */ }
          }
          setGuestMode(false);
          gs.clear();
        } catch { /* noop */ }

        // Clean URL
        window.history.replaceState({}, document.title, "/auth/google/callback");
        const next = data?.next || "/home";
        const user = data?.user;
        if (!user?.onboarding_complete) navigate("/onboarding", { replace: true });
        else if (user?.is_superadmin) navigate("/superadmin", { replace: true });
        else if (user?.role === "admin") navigate("/admin/professionals", { replace: true });
        else navigate(next, { replace: true });
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || "We couldn't sign you in. Please try again.";
        setError(msg);
      });
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen grid place-items-center bg-canvas" data-testid="google-callback">
      <div className="text-center">
        <div className="font-display text-[24px]">Signing you in</div>
        <p className="text-sm text-ink-muted mt-1">Just a second…</p>
        {error && (
          <>
            <p className="text-sm text-red-600 mt-3">{error}</p>
            <button
              onClick={() => navigate("/login")}
              data-testid="google-callback-back"
              className="mt-4 text-[14px] underline"
            >
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
